import React, { useState, useEffect, useCallback } from 'react';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { projectService, taskService } from '../../projects';
import { resourceService } from '../../resources';
import { Users, TrendingUp, Activity, Calendar, Database, Download } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import ProjectProgressChart from '../components/ProjectProgressChart';
import ResourceUtilizationChart from '../components/ResourceUtilizationChart';
import ReportsGenerator from '../components/ReportsGenerator';
import { reportsService } from '../api/reportsApi';
import { toast } from 'react-hot-toast';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const normalizeListResponse = (payload, key) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.[key])) return payload[key];
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const isMilestoneCompleted = (m, projectTasks) => {
  if (m.progress === 100) return true;
  if (m.status === 'completed' || m.status === 'done' || m.column === 'done') return true;
  if (typeof m.progress === 'string' && parseInt(m.progress) === 100) return true;
  if (m.tasks?.length > 0) {
    const done = m.tasks.filter(t => t.status === 'completed' || t.status === 'done').length;
    if (done === m.tasks.length && done > 0) return true;
  }
  if (m.id && projectTasks?.length > 0) {
    const mTasks = projectTasks.filter(t => t.milestoneId === m.id);
    if (mTasks.length > 0 && mTasks.every(t => t.status === 'done' || t.status === 'completed')) return true;
  }
  return false;
};

const resourceStatusStyle = (status) => {
  if (status === 'available')          return { backgroundColor: SYSTEM_COLORS.successTint, color: SYSTEM_COLORS.success };
  if (status === 'partiallyAvailable') return { backgroundColor: SYSTEM_COLORS.warningTint, color: SYSTEM_COLORS.warning };
  return { backgroundColor: SYSTEM_COLORS.dangerTint, color: SYSTEM_COLORS.danger };
};

// ─── Reports ──────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'projects',  label: 'Projects',  icon: Activity  },
  { id: 'resources', label: 'Resources', icon: Database   },
  { id: 'team',      label: 'Team',      icon: Users      },
  { id: 'generate',  label: 'Generate',  icon: Download   },
  { id: 'timeline',  label: 'Timeline',  icon: Calendar   },
];

const Reports = () => {
  const { user } = useApiAuth();
  const location = useLocation();

  // Page-level data for Projects + Resources tabs
  const [loading,           setLoading]           = useState(true);
  const [activeTab,         setActiveTab]         = useState('projects');
  const [activeProjectTab,  setActiveProjectTab]  = useState('graph');
  const [data,              setData]              = useState({ projects: [], resources: [], bookings: [] });

  // Team Activity tab
  const [teamActivity,        setTeamActivity]        = useState(null);
  const [teamActivityLoading, setTeamActivityLoading] = useState(false);
  const [teamFrom,            setTeamFrom]            = useState('');
  const [teamTo,              setTeamTo]              = useState('');

  // Timeline tab
  const [timelineData,      setTimelineData]      = useState([]);
  const [timelineLoading,   setTimelineLoading]   = useState(false);
  const [timelineProjectId, setTimelineProjectId] = useState('');

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab === 'overview' ? 'projects' : location.state.activeTab);
    }
  }, [location]);

  // Page-level load: Projects + Resources only
  useEffect(() => {
    if (!user) return;
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const [projectsPayload, resourcesPayload, bookingsPayload] = await Promise.all([
          projectService.getAllProjects(),
          resourceService.getResources(),
          resourceService.getBookings(),
        ]);

        const projectsData  = normalizeListResponse(projectsPayload,  'projects');
        const resourcesData = normalizeListResponse(resourcesPayload,  'resources');
        const bookingsData  = normalizeListResponse(bookingsPayload,   'bookings');

        const projectsWithTasks = await Promise.all(
          projectsData.map(async (project) => {
            try {
              const taskPayload = await taskService.getProjectTasks(project.id);
              return { ...project, tasks: normalizeListResponse(taskPayload, 'tasks') };
            } catch { return project; }
          })
        );

        if (mounted) setData({ projects: projectsWithTasks, resources: resourcesData, bookings: bookingsData });
      } catch {
        toast.error('Failed to load report data');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [user]);

  // Team Activity: lazy fetch, re-runs when date range changes
  const fetchTeamActivity = useCallback(async () => {
    setTeamActivityLoading(true);
    try {
      const result = await reportsService.getTeamActivity({
        from: teamFrom || undefined,
        to:   teamTo   || undefined,
      });
      setTeamActivity(result);
    } catch (err) {
      toast.error(err.message || 'Failed to load team activity');
    } finally {
      setTeamActivityLoading(false);
    }
  }, [teamFrom, teamTo]);

  useEffect(() => {
    if (activeTab === 'team') fetchTeamActivity();
  }, [activeTab, fetchTeamActivity]);

  // Timeline: lazy fetch, re-runs when project filter changes
  const fetchTimeline = useCallback(async () => {
    setTimelineLoading(true);
    try {
      const result = await reportsService.getTimeline({
        projectId: timelineProjectId || undefined,
      });
      setTimelineData(Array.isArray(result) ? result : []);
    } catch (err) {
      toast.error(err.message || 'Failed to load timeline');
    } finally {
      setTimelineLoading(false);
    }
  }, [timelineProjectId]);

  useEffect(() => {
    if (activeTab === 'timeline') fetchTimeline();
  }, [activeTab, fetchTimeline]);

  // ── Chart data ────────────────────────────────────────────────────────────
  const progressData = data.projects.map(project => {
    if (!project.milestones?.length) return { name: project.name, completed: 0, inProgress: 0 };
    const completed  = project.milestones.filter(m => isMilestoneCompleted(m, project.tasks)).length;
    const inProgress = project.milestones.length - completed;
    return { name: project.name, completed, inProgress };
  });

  const resourceData = data.resources.length > 0
    ? data.resources.map(r => ({ resource: r.name, used: r.utilization, available: 100 - r.utilization }))
    : [
        { resource: 'Equipment',         used: 75, available: 25 },
        { resource: 'Software Licenses', used: 60, available: 40 },
        { resource: 'Meeting Rooms',     used: 85, available: 15 },
      ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-b-2 rounded-full animate-spin" style={{ borderColor: BRAND_COLORS.primary }} />
      </div>
    );
  }

  const thCell = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider';

  return (
    <div className="space-y-5">

      {/* Header */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: BRAND_COLORS.primary }}>
          Insights
        </p>
        <h1 className="mt-1 text-2xl font-bold" style={{ color: NEUTRAL_COLORS.black }}>
          Reports & Analytics
        </h1>
      </div>

      {/* Tab panel */}
      <div className="rounded-xl border overflow-hidden"
        style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>

        {/* Tab bar */}
        <div className="flex overflow-x-auto border-b" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          {TABS.map(({ id, label, icon: Icon }) => {
            const active = activeTab === id;
            return (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className="flex items-center gap-2 px-5 py-3.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors"
                style={{
                  borderBottomColor: active ? BRAND_COLORS.primary : 'transparent',
                  color: active ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted,
                  backgroundColor: active ? SYSTEM_COLORS.shellSurfaceMuted : 'transparent',
                }}
              >
                <Icon size={15} />
                {label}
              </button>
            );
          })}
        </div>

        <div className="p-5 sm:p-6">

          {/* ── Projects tab ─────────────────────────────────────────────── */}
          {activeTab === 'projects' && (
            <div className="space-y-4">
              <div className="flex border-b" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                {[
                  { id: 'graph',      label: 'Graph',               icon: TrendingUp },
                  { id: 'milestones', label: 'Milestone Completion', icon: Activity   },
                ].map(({ id, label, icon: Icon }) => {
                  const active = activeProjectTab === id;
                  return (
                    <button
                      key={id}
                      onClick={() => setActiveProjectTab(id)}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors"
                      style={{
                        borderBottomColor: active ? BRAND_COLORS.primary : 'transparent',
                        color: active ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted,
                      }}
                    >
                      <Icon size={14} /> {label}
                    </button>
                  );
                })}
              </div>

              {activeProjectTab === 'graph' && <ProjectProgressChart data={progressData} />}

              {activeProjectTab === 'milestones' && (
                <div className="overflow-x-auto rounded-xl border" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                  <table className="min-w-full">
                    <thead style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                      <tr>
                        {['Project', 'Total Milestones', 'Completed', 'In Progress', 'Completion Rate'].map(h => (
                          <th key={h} className={thCell} style={{ color: SYSTEM_COLORS.textMuted }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                      {data.projects.map(project => {
                        const total     = project.milestones?.length || 0;
                        const completed = project.milestones?.filter(m => isMilestoneCompleted(m, project.tasks)).length || 0;
                        const inProg    = total - completed;
                        const rate      = total ? Math.round((completed / total) * 100) : 0;
                        return (
                          <tr key={project.id}>
                            <td className="px-4 py-3 text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>{project.name}</td>
                            <td className="px-4 py-3 text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>{total}</td>
                            <td className="px-4 py-3">
                              <span className="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold"
                                style={{ backgroundColor: SYSTEM_COLORS.successTint, color: SYSTEM_COLORS.success }}>
                                {completed}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold"
                                style={{ backgroundColor: SYSTEM_COLORS.infoTint, color: SYSTEM_COLORS.info }}>
                                {inProg}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="h-2 rounded-full flex-1 max-w-[80px]" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
                                  <div className="h-2 rounded-full" style={{ width: `${rate}%`, backgroundColor: BRAND_COLORS.primary }} />
                                </div>
                                <span className="text-sm" style={{ color: NEUTRAL_COLORS.black }}>{rate}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Resources tab ─────────────────────────────────────────────── */}
          {activeTab === 'resources' && (
            <div className="space-y-5">
              <p className="text-base font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Resource Utilization</p>
              <ResourceUtilizationChart data={resourceData} />

              <div>
                <p className="text-sm font-semibold mb-3" style={{ color: NEUTRAL_COLORS.black }}>Resource Allocation by Project</p>
                <div className="overflow-x-auto rounded-xl border" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                  <table className="min-w-full">
                    <thead style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                      <tr>
                        {['Resource', 'Type', 'Utilization', 'Assigned Projects', 'Status'].map(h => (
                          <th key={h} className={thCell} style={{ color: SYSTEM_COLORS.textMuted }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                      {data.resources.map(resource => (
                        <tr key={resource.id}>
                          <td className="px-4 py-3 text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>{resource.name}</td>
                          <td className="px-4 py-3 text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>{resource.type || 'N/A'}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="h-2 rounded-full max-w-[80px] flex-1" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
                                <div className="h-2 rounded-full" style={{ width: `${resource.utilization}%`, backgroundColor: BRAND_COLORS.primary }} />
                              </div>
                              <span className="text-sm" style={{ color: NEUTRAL_COLORS.black }}>{resource.utilization}%</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>{resource.projects?.length || 0} projects</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize"
                              style={resourceStatusStyle(resource.statusLabel)}>
                              {resource.statusLabel || 'Unknown'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── Team Activity tab ─────────────────────────────────────────── */}
          {activeTab === 'team' && (
            <div className="space-y-5">
              {/* Header + date range */}
              <div className="flex flex-wrap gap-3 items-center">
                <p className="text-base font-semibold flex-1" style={{ color: NEUTRAL_COLORS.black }}>Team Activity</p>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <input
                    type="date"
                    value={teamFrom}
                    onChange={e => setTeamFrom(e.target.value)}
                    className="rounded-xl border px-3 py-1.5 text-sm outline-none"
                    style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface, color: NEUTRAL_COLORS.black }}
                  />
                  <span className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>to</span>
                  <input
                    type="date"
                    value={teamTo}
                    onChange={e => setTeamTo(e.target.value)}
                    className="rounded-xl border px-3 py-1.5 text-sm outline-none"
                    style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface, color: NEUTRAL_COLORS.black }}
                  />
                </div>
              </div>

              {teamActivityLoading ? (
                <div className="flex items-center justify-center h-40">
                  <div className="w-8 h-8 border-b-2 rounded-full animate-spin" style={{ borderColor: BRAND_COLORS.primary }} />
                </div>
              ) : !teamActivity ? (
                <div className="flex items-center justify-center h-40 rounded-xl border"
                  style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                  <p className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>No data available</p>
                </div>
              ) : (
                <>
                  {/* KPI chips */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[
                      { label: 'Members',            value: teamActivity.memberCount,                 tint: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary },
                      { label: 'Tasks Completed',    value: teamActivity.teamTotalTasksCompleted,      tint: SYSTEM_COLORS.successTint, color: SYSTEM_COLORS.success },
                      { label: 'Milestones Done',    value: teamActivity.teamTotalMilestonesCompleted, tint: SYSTEM_COLORS.infoTint,    color: SYSTEM_COLORS.info    },
                      { label: 'Avg Score',          value: `${(teamActivity.averageActivityScore || 0).toFixed(1)}/10`, tint: '#f3e8ff', color: '#7e22ce' },
                    ].map(({ label, value, tint, color }) => (
                      <div key={label} className="px-4 py-4 rounded-xl"
                        style={{ backgroundColor: tint }}>
                        <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color }}>{label}</p>
                        <p className="text-2xl font-bold" style={{ color }}>{value ?? '—'}</p>
                      </div>
                    ))}
                  </div>

                  {/* Leaderboard table */}
                  <div className="overflow-x-auto rounded-xl border" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                    <table className="min-w-full">
                      <thead style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                        <tr>
                          {['#', 'Member', 'Role', 'Tasks', 'Milestones', 'Comments', 'Docs', 'Projects', 'Actions', 'Score'].map(h => (
                            <th key={h} className={thCell} style={{ color: SYSTEM_COLORS.textMuted }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                        {(teamActivity.members || []).map((member, idx) => {
                          const score = member.metrics?.activityScore || 0;
                          return (
                            <tr key={member.userId}>
                              <td className="px-4 py-3 text-xs font-semibold" style={{ color: SYSTEM_COLORS.textMuted }}>{idx + 1}</td>
                              <td className="px-4 py-3">
                                <p className="text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>{member.name}</p>
                                <p className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>{member.email}</p>
                              </td>
                              <td className="px-4 py-3 text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>{member.role || '—'}</td>
                              <td className="px-4 py-3 text-sm text-center" style={{ color: NEUTRAL_COLORS.black }}>{member.metrics?.tasksCompleted ?? 0}</td>
                              <td className="px-4 py-3 text-sm text-center" style={{ color: NEUTRAL_COLORS.black }}>{member.metrics?.milestonesCompleted ?? 0}</td>
                              <td className="px-4 py-3 text-sm text-center" style={{ color: NEUTRAL_COLORS.black }}>{member.metrics?.commentsAdded ?? 0}</td>
                              <td className="px-4 py-3 text-sm text-center" style={{ color: NEUTRAL_COLORS.black }}>{member.metrics?.docsUploaded ?? 0}</td>
                              <td className="px-4 py-3 text-sm text-center" style={{ color: NEUTRAL_COLORS.black }}>{member.metrics?.projectsContributedTo ?? 0}</td>
                              <td className="px-4 py-3 text-sm text-center" style={{ color: NEUTRAL_COLORS.black }}>{member.metrics?.totalActions ?? 0}</td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <div className="h-2 rounded-full max-w-[60px] flex-1" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
                                    <div className="h-2 rounded-full"
                                      style={{ width: `${(score / 10) * 100}%`, backgroundColor: BRAND_COLORS.primary }} />
                                  </div>
                                  <span className="text-xs font-semibold w-8 text-right" style={{ color: NEUTRAL_COLORS.black }}>
                                    {score.toFixed(1)}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {(teamActivity.members || []).length === 0 && (
                      <div className="py-10 text-center text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
                        No activity data for this window
                      </div>
                    )}
                  </div>

                  {/* Window info */}
                  {(teamActivity.windowFrom || teamActivity.windowTo) && (
                    <p className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
                      Window: {teamActivity.windowFrom ? new Date(teamActivity.windowFrom).toLocaleDateString() : '—'}
                      {' — '}
                      {teamActivity.windowTo ? new Date(teamActivity.windowTo).toLocaleDateString() : '—'}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Generate tab ─────────────────────────────────────────────── */}
          {activeTab === 'generate' && <ReportsGenerator />}

          {/* ── Timeline tab ─────────────────────────────────────────────── */}
          {activeTab === 'timeline' && (
            <div className="space-y-5">
              {/* Header + project filter */}
              <div className="flex flex-wrap gap-3 items-center">
                <p className="text-base font-semibold flex-1" style={{ color: NEUTRAL_COLORS.black }}>Project Timeline</p>
                <select
                  value={timelineProjectId}
                  onChange={e => setTimelineProjectId(e.target.value)}
                  className="rounded-xl border px-3 py-1.5 text-sm outline-none flex-shrink-0"
                  style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface, color: NEUTRAL_COLORS.black }}
                >
                  <option value="">All Projects</option>
                  {data.projects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              {timelineLoading ? (
                <div className="flex items-center justify-center h-40">
                  <div className="w-8 h-8 border-b-2 rounded-full animate-spin" style={{ borderColor: BRAND_COLORS.primary }} />
                </div>
              ) : timelineData.length === 0 ? (
                <div className="py-12 text-center rounded-xl border"
                  style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                  <p className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>No timeline data available</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {timelineData.map(project => {
                    const milestones = [...(project.milestones || [])].sort((a, b) => {
                      if (!a.dueDate) return 1;
                      if (!b.dueDate) return -1;
                      return new Date(a.dueDate) - new Date(b.dueDate);
                    });
                    const tasks = [...(project.tasks || [])].sort((a, b) => {
                      if (!a.dueDate) return 1;
                      if (!b.dueDate) return -1;
                      return new Date(a.dueDate) - new Date(b.dueDate);
                    });

                    return (
                      <div key={project.projectId} className="rounded-xl border p-5"
                        style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>

                        {/* Project header */}
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div>
                            <h4 className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>{project.projectName}</h4>
                            <p className="text-xs mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>
                              {project.startDate ? new Date(project.startDate).toLocaleDateString() : 'No start'}
                              {' → '}
                              {project.endDate ? new Date(project.endDate).toLocaleDateString() : 'No end'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize"
                              style={{ backgroundColor: SYSTEM_COLORS.infoTint, color: SYSTEM_COLORS.info }}>
                              {project.status || 'active'}
                            </span>
                            <span className="text-xs font-bold" style={{ color: BRAND_COLORS.primary }}>
                              {project.progress ?? 0}%
                            </span>
                          </div>
                        </div>

                        {/* Overall progress bar */}
                        <div className="h-2 rounded-full mb-5" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
                          <div className="h-2 rounded-full transition-all"
                            style={{ width: `${project.progress ?? 0}%`, backgroundColor: BRAND_COLORS.primary }} />
                        </div>

                        {/* Milestones */}
                        {milestones.length > 0 && (
                          <div className="mb-5">
                            <p className="text-xs font-semibold mb-3" style={{ color: SYSTEM_COLORS.textMuted }}>
                              Milestones ({milestones.length})
                            </p>
                            <div className="relative">
                              <div className="absolute left-3 top-0 bottom-0 w-0.5"
                                style={{ backgroundColor: SYSTEM_COLORS.borderSoft }} />
                              <div className="space-y-3">
                                {milestones.map((m, i) => {
                                  const isDone = m.status === 'completed' || m.status === 'done' || m.progress === 100;
                                  const dotBg  = isDone ? SYSTEM_COLORS.success : m.status === 'in_progress' ? BRAND_COLORS.primary : SYSTEM_COLORS.border;
                                  return (
                                    <div key={m.id || i} className="ml-10 relative">
                                      <div
                                        className="absolute -left-10 mt-1 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                        style={{ backgroundColor: dotBg }}
                                      >
                                        {isDone ? '✓' : i + 1}
                                      </div>
                                      <div className="rounded-xl p-3" style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                                        <p className="text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>{m.title}</p>
                                        <div className="flex items-center gap-2 mt-1.5">
                                          {m.dueDate && (
                                            <span className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
                                              Due {new Date(m.dueDate).toLocaleDateString()}
                                            </span>
                                          )}
                                          <div className="flex-1 h-1.5 rounded-full" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
                                            <div className="h-1.5 rounded-full" style={{ width: `${m.progress || 0}%`, backgroundColor: dotBg }} />
                                          </div>
                                          <span className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>{m.progress || 0}%</span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Tasks */}
                        {tasks.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold mb-2" style={{ color: SYSTEM_COLORS.textMuted }}>
                              Tasks ({tasks.length})
                            </p>
                            <div className="space-y-1.5">
                              {tasks.map((task, i) => {
                                const isDone = task.status === 'completed' || task.status === 'done';
                                return (
                                  <div key={task.id || i}
                                    className="flex items-center gap-3 px-3 py-2 rounded-lg"
                                    style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                                    <div className="w-2 h-2 rounded-full flex-shrink-0"
                                      style={{ backgroundColor: isDone ? SYSTEM_COLORS.success : BRAND_COLORS.primary }} />
                                    <span className="text-xs flex-1 font-medium"
                                      style={{ color: NEUTRAL_COLORS.black, textDecoration: isDone ? 'line-through' : 'none', opacity: isDone ? 0.65 : 1 }}>
                                      {task.title}
                                    </span>
                                    {task.dueDate && (
                                      <span className="text-xs flex-shrink-0" style={{ color: SYSTEM_COLORS.textMuted }}>
                                        {new Date(task.dueDate).toLocaleDateString()}
                                      </span>
                                    )}
                                    <span className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize flex-shrink-0"
                                      style={{
                                        backgroundColor: isDone ? SYSTEM_COLORS.successTint : SYSTEM_COLORS.surfaceMuted,
                                        color: isDone ? SYSTEM_COLORS.success : SYSTEM_COLORS.textMuted,
                                      }}>
                                      {task.status || 'open'}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Reports;
